import { LDClient } from 'launchdarkly-js-client-sdk';
import { useEffect, useState } from 'react';

import { getLDClient } from '@/clients/launch-darkly-client';

/** Poll cadence + ceiling while waiting for the LD client to be constructed. */
const POLL_INTERVAL_MS = 300;
const MAX_POLL_ATTEMPTS = 10; // ~3s, then give up (LD likely disabled/absent).

/**
 * Reactive read of a boolean LaunchDarkly flag. Defaults to `false`, so with LD
 * unavailable the gated feature stays hidden.
 *
 * LD is initialized fire-and-forget in the auth guard, so at mount the client
 * may be in one of three states:
 *  1. constructed + ready — evaluate directly;
 *  2. constructed but still loading — `variation` returns the fallback and only
 *     `change` fires on *later* updates (never the initial value), so we also
 *     re-read on `ready`;
 *  3. not constructed yet (`window.cliengo_ld_client === undefined`) — the auth
 *     init races the wizard mount. Without handling this the hook would stay at
 *     the initial `false` forever, hiding the feature for a flag-ON company. We
 *     poll briefly until the client appears, then subscribe. The poll is bounded
 *     so a disabled LD (empty client id → client never built) doesn't spin.
 */
export const useFeatureFlag = (flag: string): boolean => {
  const [enabled, setEnabled] = useState<boolean>(() =>
    Boolean(getLDClient()?.evaluateFlag(flag, false))
  );

  useEffect(() => {
    const evaluate = (): void =>
      setEnabled(Boolean(getLDClient()?.evaluateFlag(flag, false)));

    const subscribe = (ld: LDClient): void => {
      // Cover LD becoming ready between the initial state and this subscription.
      evaluate();
      ld.on('ready', evaluate);
      ld.on(`change:${flag}`, evaluate);
    };

    const existing = window.cliengo_ld_client;

    if (existing) {
      subscribe(existing);

      return () => {
        existing.off('ready', evaluate);
        existing.off(`change:${flag}`, evaluate);
      };
    }

    // Client not constructed yet: poll until it appears (bounded), then subscribe.
    let attempts = 0;
    let subscribed: LDClient | undefined;
    const pollId = setInterval(() => {
      const ld = window.cliengo_ld_client;

      if (ld) {
        clearInterval(pollId);
        subscribed = ld;
        subscribe(ld);
      } else if (++attempts >= MAX_POLL_ATTEMPTS) {
        clearInterval(pollId);
      }
    }, POLL_INTERVAL_MS);

    return () => {
      clearInterval(pollId);
      if (subscribed) {
        subscribed.off('ready', evaluate);
        subscribed.off(`change:${flag}`, evaluate);
      }
    };
  }, [flag]);

  return enabled;
};
